import React, { FunctionComponent, useState, useEffect } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { caseRoute, tracingsRoute } from 'helpers/routing'
import { Col, Row, Form } from 'reactstrap'
import Divider from 'components/common/Divider'
import FormField, { AutoCompleteFormField } from 'components/common/FormField'
import PrimaryButton from 'components/common/PrimaryButton'
import SecondaryButton from 'components/common/SecondaryButton'
import LayoutTitle from 'teleporters/LayoutTitle'
import PageTitle from 'components/common/PageTitle'
import PageBreadcrumbs from 'components/common/PageBreadcrumbs'
import { DefaultDatePicker } from 'components/common/DatePicker'
import { CaseCreateInput, Query } from 'generated/types'
import { useForm } from 'react-hook-form'
import { CASES_QUERY } from './list'

const SEARCH_USERS = gql`
  query Users($search: String!) {
    users(search: $search) {
      id
      email
    }
  }
`

const CREATE_CASE_MUTATION = gql`
  mutation createOneCase($input: CaseCreateInput) {
    createOneCase(input: $input) {
      id
    }
  }
`

interface CreateCaseFormData {
  email: string
  notes?: string
}

export const CreateCasePage: FunctionComponent = () => {
  const history = useHistory()
  const [createCase] = useMutation(CREATE_CASE_MUTATION, {
    refetchQueries: [{ query: CASES_QUERY }],
    errorPolicy: 'all',
  })

  const {
    handleSubmit, register, errors, setError, setValue,
  } = useForm<CreateCaseFormData>()

  const [caseReportDate, setCaseReportDate] = useState<Date>(new Date())

  const onSubmit = async (data: CreateCaseFormData) => {
    const input: CaseCreateInput = {
      email: data.email,
      date: caseReportDate,
      notes: data.notes,
    }

    const resp = await createCase({
      variables: { input },
    })

    if (resp.errors) {
      console.error(resp.errors)
      if (resp.errors[0].message.includes('Not Found')) {
        setError('email', {
          type: 'manual',
          message: 'Could not find a user with that email',
        })
        return
      }
      // otherwise an unknown error happened
      setError('email', {
        type: 'manual',
        message: 'An unknown error has occurred.',
      })
      return
    }

    history.push(caseRoute(resp.data.createOneCase.id))
  }

  const [searchFilter, setSearchFilter] = useState('')

  const users = useQuery<Query>(SEARCH_USERS, {
    variables: { search: searchFilter },
  })

  const searchedUsers = (users.data?.users || []).map((user) => {
    return { value: user.email }
  })

  const [options, setOptions] = useState<{ value: string }[]>(searchedUsers)

  useEffect(() => {
    register({ name: 'email' }, { required: 'Email is required' })
  }, [register])

  const onSearch = (searchText: string) => {
    setValue('email', searchText, { shouldValidate: true, shouldDirty: true })
    setSearchFilter(searchText.toLowerCase())
    setOptions(searchedUsers)
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <LayoutTitle.Source>
        <PageBreadcrumbs routeTo="/tracing">
          Tracing
        </PageBreadcrumbs>
        <PageTitle>Create Case</PageTitle>
      </LayoutTitle.Source>

      <Divider />

      <Row>
        <Col xs="12" sm="12" lg="5">
          <AutoCompleteFormField
            label="User Email"
            // We use options instead of just passing searchedUsers here
            // to fix flickering of dropdown
            options={options}
            style={{ width: '100%' }}
            onSearch={onSearch}
            feedback={errors.email?.message}
          />
          <DefaultDatePicker
            id="report-date"
            label="Report Date"
            date={caseReportDate}
            onDateChange={(d) => {
              setCaseReportDate(d!)
            }}
          />
          <div className="mt-4" />
          <FormField
            label="Notes"
            id="notes"
            name="notes"
            innerRef={register()}
            type="textarea"
            feedback={errors.notes?.message}
            invalid={Boolean(errors.notes)}
            placeholder="add notes"
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs="12">
          <SecondaryButton
            title="Cancel"
            to={tracingsRoute()}
            buttonProps={{ className: 'mr-4' }}
          />
          <PrimaryButton
            title="Add Case"
          />
        </Col>
      </Row>

    </Form>
  )
}

export default CreateCasePage
